import { useCallback, useMemo, useState } from 'react';
import adminService from '@wlb/shared/api/admin/adminService';
import feedService from '@wlb/shared/api/services/feedService';
import type { FeedPost } from '@wlb/shared/types/post';
import type { FeedTab } from '@wlb/shared/types/feed';
import useDebounce from '@wlb/shared/hooks/useDebounce';
import { ToastService } from '@wlb/shared/platform/notifier';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { DataTable, type DataTableColumn } from '../components/ui/DataTable';
import { Icon } from '../components/ui/Icon';
import { IconButton } from '../components/ui/IconButton';
import { FormError } from '../components/ui/FormError';
import { PageHeader } from '../components/layout/PageHeader';
import { Modal } from '../components/ui/Modal';
import { SelectField } from '../components/ui/SelectField';
import { StateBlock } from '../components/ui/StateBlock';
import { TextField } from '../components/ui/TextField';
import { AdminPostCard } from '../features/posts/AdminPostCard';
import { usePagedList } from '../hooks/usePagedList';
import { useScrollReset } from '../hooks/useScrollReset';
import { formatCount, formatDateTime } from '../utils/format';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import '../components/ui/ui.css';
import '../features/posts/post-card.css';
import './pages.css';

type ViewMode = 'table' | 'cards';

const FEED_TABS: readonly FeedTab[] = ['wlb', 'trending'];

export function Posts() {
  const { t } = useTranslation('admin');
  const [feedTab, setFeedTab] = useState<FeedTab>('wlb');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [pending, setPending] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const debouncedSearch = useDebounce(search);

  const loadPage = useCallback(
    async (page: number) => {
      const response = await feedService.getFeed(
        feedTab,
        page,
        debouncedSearch.trim() || undefined,
      );
      return { items: response.data ?? [], hasNextPage: response.hasNextPage ?? false };
    },
    [debouncedSearch, feedTab],
  );

  const requestKey = [feedTab, debouncedSearch.trim()].join('|');

  const {
    items: rows,
    status,
    refreshing,
    loadingMore,
    hasNextPage,
    error,
    loadMore,
    reload,
  } = usePagedList(loadPage, requestKey, t('common.errorTitle'));

  const feedRef = useScrollReset<HTMLDivElement>(requestKey);

  const selectedIds = useMemo(
    () => rows.filter(row => selected.has(row.id)).map(row => row.id),
    [rows, selected],
  );

  const toggleRow = useCallback((key: string) => {
    setSelected(current => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected(current =>
      current.size === rows.length
        ? new Set<string>()
        : new Set(rows.map(row => row.id)),
    );
  }, [rows]);

  const afterAction = useCallback(() => {
    setSelected(new Set());
    reload();
  }, [reload]);

  const requireSelection = (): boolean => {
    if (selectedIds.length === 0) {
      ToastService.show({ type: 'warning', message: t('posts.selectionRequired') });
      return false;
    }
    return true;
  };

  const changeVisibility = async (isActive: boolean) => {
    if (!requireSelection()) {
      return;
    }
    const postIds = selectedIds
      .map(id => Number(id))
      .filter(id => Number.isSafeInteger(id) && id > 0);
    if (postIds.length === 0) {
      ToastService.show({ type: 'warning', message: t('posts.unsupportedSelection') });
      return;
    }
    const skipped = selectedIds.length - postIds.length;
    setPending(true);
    try {
      await adminService.setPostsVisibility(postIds, isActive);
      ToastService.show({
        type: skipped > 0 ? 'warning' : 'success',
        message:
          skipped > 0
            ? t('posts.toasts.visibilityPartial', {
                count: postIds.length,
                total: selectedIds.length,
              })
            : isActive
              ? t('posts.toasts.restored')
              : t('posts.toasts.hidden'),
      });
      afterAction();
    } catch (cause) {
      ToastService.show({
        type: 'error',
        message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
      });
    } finally {
      setPending(false);
    }
  };

  const hideSingle = useCallback(
    async (post: FeedPost) => {
      const postId = Number(post.id);
      if (!Number.isSafeInteger(postId) || postId <= 0) {
        ToastService.show({ type: 'warning', message: t('posts.unsupportedSelection') });
        return;
      }
      setPending(true);
      try {
        await adminService.setPostsVisibility([postId], false);
        ToastService.show({ type: 'success', message: t('posts.toasts.hidden') });
        reload();
      } catch (cause) {
        ToastService.show({
          type: 'error',
          message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
        });
      } finally {
        setPending(false);
      }
    },
    [reload, t],
  );

  const confirmDeleteSingle = useCallback(
    (post: FeedPost) => {
      setSelected(new Set([post.id]));
      setConfirmDelete(true);
    },
    [],
  );

  const deleteSelected = async () => {
    setPending(true);
    try {
      const results = await Promise.all(
        selectedIds.map(id => feedService.deletePost(id)),
      );
      const deleted = results.filter(result => result.success).length;
      ToastService.show({
        type: deleted === results.length ? 'success' : 'warning',
        message:
          deleted === results.length
            ? t('posts.toasts.deleted')
            : t('posts.toasts.deletedPartial', {
                count: deleted,
                total: results.length,
              }),
      });
      setConfirmDelete(false);
      afterAction();
    } catch (cause) {
      ToastService.show({
        type: 'error',
        message: getUserFacingErrorMessage(cause, t('common.errorTitle')),
      });
    } finally {
      setPending(false);
    }
  };

  const columns = useMemo<readonly DataTableColumn<FeedPost>[]>(
    () => [
      {
        key: 'post',
        header: t('posts.columns.post'),
        render: post => (
          <span className="ui-table__truncate">
            {post.text || post.title || t('common.none')}
          </span>
        ),
      },
      {
        key: 'author',
        header: t('posts.columns.author'),
        render: post => (
          <div className="ui-table__cell-with-avatar">
            <Avatar
              src={resolveImageUrl(post.avatar)}
              name={post.username}
            />
            <span>
              <span className="ui-table__primary">{post.username}</span>
              {post.groupName ? (
                <span className="ui-table__secondary">{post.groupName}</span>
              ) : null}
            </span>
          </div>
        ),
      },
      {
        key: 'type',
        header: t('posts.columns.type'),
        render: post =>
          post.isBlog
            ? t('posts.type.blog')
            : post.groupName
              ? t('posts.type.group')
              : t('posts.type.post'),
      },
      {
        key: 'posted',
        header: t('posts.columns.posted'),
        render: post => formatDateTime(post.postDate) || post.timeAgo || t('common.none'),
      },
      {
        key: 'engagement',
        header: t('posts.columns.engagement'),
        render: post =>
          t('posts.engagement.value', {
            likes: formatCount(post.likes),
            comments: formatCount(post.comments),
            shares: formatCount(post.shares),
          }),
      },
      {
        key: 'actions',
        header: t('common.actions'),
        render: post => (
          <div className="ui-table__actions">
            <IconButton
              icon="eyeOff"
              tone="amber"
              label={t('posts.actions.hide')}
              disabled={pending}
              onClick={() => hideSingle(post)}
            />
            <IconButton
              icon="trash"
              tone="rose"
              label={t('posts.actions.delete')}
              disabled={pending}
              onClick={() => confirmDeleteSingle(post)}
            />
          </div>
        ),
      },
    ],
    [confirmDeleteSingle, hideSingle, pending, t],
  );

  const changeQuery = (apply: () => void) => {
    setSelected(new Set());
    apply();
  };

  /* The end of the list rather than a page strip: a page of cards is taller than
     the column, so numbered paging meant scrolling to the bottom, clicking and
     scrolling back up. A failed step keeps the posts already listed and offers
     the retry here, where the operator is looking. */
  const loadMoreFailed = Boolean(error) && status === 'ready';
  const loadMoreControls =
    hasNextPage || loadMoreFailed ? (
      <>
        {loadMoreFailed ? <FormError message={error} /> : null}
        {hasNextPage ? (
          <Button
            variant="secondary"
            small
            disabled={loadingMore}
            onClick={loadMore}>
            {loadingMore
              ? t('common.loading')
              : loadMoreFailed
                ? t('common.retry')
                : t('common.loadMore')}
          </Button>
        ) : null}
      </>
    ) : null;

  return (
    <div className="page">
      <PageHeader
        title={t('posts.title')}
        subtitle={t('posts.subtitle')}
        actions={
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            disabled={refreshing}
            onClick={reload}
          />
        }
      />

      <section className="page__toolbar" aria-label={t('common.filters')}>
        <div className="page__toolbar-field">
          <SelectField
            label={t('posts.filters.feedLabel')}
            value={feedTab}
            options={FEED_TABS.map(tab => ({
              value: tab,
              label: t(`posts.feeds.${tab}`),
            }))}
            onChange={event => changeQuery(() => setFeedTab(event.target.value))}
          />
        </div>
        <div className="page__toolbar-field page__toolbar-field--wide">
          <TextField
            label={t('posts.filters.searchLabel')}
            type="search"
            placeholder={t('posts.filters.searchPlaceholder')}
            value={search}
            onChange={event => changeQuery(() => setSearch(event.target.value))}
          />
        </div>
        <div className="page__toolbar-field">
          <div className="ui-field">
            <span className="ui-field__label">{t('common.filters')}</span>
            <div className="ui-view-toggle">
              <button
                type="button"
                className={
                  viewMode === 'cards'
                    ? 'ui-view-toggle__btn ui-view-toggle__btn--active'
                    : 'ui-view-toggle__btn'
                }
                aria-pressed={viewMode === 'cards'}
                onClick={() => setViewMode('cards')}>
                {t('posts.viewCards')}
              </button>
              <button
                type="button"
                className={
                  viewMode === 'table'
                    ? 'ui-view-toggle__btn ui-view-toggle__btn--active'
                    : 'ui-view-toggle__btn'
                }
                aria-pressed={viewMode === 'table'}
                onClick={() => setViewMode('table')}>
                {t('posts.viewTable')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {selected.size > 0 ? (
        <div className="page__bulk-bar">
          <span>{t('common.selectedCount', { count: selected.size })}</span>
          <div className="page__bulk-actions">
            <Button
              variant="secondary"
              small
              disabled={pending}
              onClick={() => changeVisibility(true)}>
              {t('posts.actions.restore')}
            </Button>
            <Button
              variant="secondary"
              small
              disabled={pending}
              onClick={() => changeVisibility(false)}>
              {t('posts.actions.hide')}
            </Button>
            <Button
              variant="danger"
              small
              disabled={pending}
              onClick={() => {
                if (requireSelection()) {
                  setConfirmDelete(true);
                }
              }}>
              {t('posts.actions.delete')}
            </Button>
          </div>
        </div>
      ) : null}

      {viewMode === 'table' ? (
        <DataTable
          caption={t('posts.title')}
          columns={columns}
          rows={rows}
          rowKey={post => post.id}
          status={status}
          refreshing={refreshing}
          error={error}
          onRetry={reload}
          emptyTitle={t('posts.emptyTitle')}
          emptyMessage={t('posts.emptyMessage')}
          // Appended pages must not move the body; a new query must reset it.
          scrollResetKey={requestKey}
          selection={{
            selected,
            onToggle: toggleRow,
            onToggleAll: toggleAll,
            rowLabel: post => post.text || post.title || String(post.id),
          }}
          footer={
            loadMoreControls ? (
              <div className="ui-pagination">{loadMoreControls}</div>
            ) : null
          }
        />
      ) : status === 'loading' ? (
        <div className="page__feed">
          <div className="ui-table-panel">
            <StateBlock status="loading" />
          </div>
        </div>
      ) : status === 'error' ? (
        <div className="page__feed">
          <div className="ui-table-panel">
            <StateBlock status="error" message={error} onRetry={reload} />
          </div>
        </div>
      ) : rows.length === 0 ? (
        <div className="page__feed">
          <div className="ui-table-panel">
            <StateBlock
              status="empty"
              title={t('posts.emptyTitle')}
              message={t('posts.emptyMessage')}
            />
          </div>
        </div>
      ) : (
        <div className="page__feed">
          <div className="admin-post-list" aria-busy={refreshing || undefined} ref={feedRef}>
            {rows.map(post => (
              <AdminPostCard
                key={post.id}
                post={post}
                selection={{
                  selected: selected.has(post.id),
                  onToggle: () => toggleRow(post.id),
                }}
                actions={
                  <>
                    <Button
                      variant="danger"
                      small
                      disabled={pending}
                      onClick={() => hideSingle(post)}>
                      <Icon name="eyeOff" size="sm" />
                      {t('posts.actions.hideOne')}
                    </Button>
                    <Button
                      variant="danger"
                      small
                      disabled={pending}
                      onClick={() => confirmDeleteSingle(post)}>
                      <Icon name="trash" size="sm" />
                      {t('posts.actions.deleteOne')}
                    </Button>
                  </>
                }
              />
            ))}
            {loadMoreControls ? (
              <div className="admin-post-list__more">{loadMoreControls}</div>
            ) : null}
          </div>
        </div>
      )}

      <Modal
        open={confirmDelete}
        title={t('posts.deleteDialog.title')}
        description={t('common.selectedCount', { count: selectedIds.length })}
        onClose={() => setConfirmDelete(false)}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={pending}
              onClick={() => setConfirmDelete(false)}>
              {t('common.cancel')}
            </Button>
            <Button variant="danger" disabled={pending} onClick={deleteSelected}>
              {pending ? t('common.saving') : t('posts.deleteDialog.confirm')}
            </Button>
          </>
        }>
        <p>{t('posts.deleteDialog.description')}</p>
      </Modal>
    </div>
  );
}
